import { Grid, Typography } from '@mui/material';
import React from 'react';
import { platformToCategoryMap, subPlatformOptions } from '../../../../static-data/configDropdownOptions';
import { formatCountryOptions } from '../../../../static-data/formatCountryOptions';
import MultiSelectDropdown from '../../../../ui/primitives/MultiSelectDropdown';

const toOptions = (arr = []) => arr.map((s) => ({ id: s, name: s }));

const StaticField = ({ label, value, required }) => (
  <div>
    <Typography
      variant="body2"
      color="text.secondary"
      gutterBottom
      sx={required ? { '&::after': { content: '" *"', color: 'error.main' } } : {}}
    >
      {label}
    </Typography>
    <Typography variant="body1" className="form-static-text">
      {value || 'None'}
    </Typography>
  </div>
);

const PlatformSelection = ({
  platforms,
  subPlatforms,
  browsers,
  projectConfig,
  isEditing,
  setFieldValue,
  isCycleCompleted,
  geographies,
  isPlatformRequired = true,
  browserOptions,
}) => {
  const getFilteredSubPlatforms = (platforms) => {
    const selectedPlatforms = platforms || [];
    const savedSubPlatforms = projectConfig?.sub_platforms || [];

    const selectedCategories = selectedPlatforms
      .map((platform) => platformToCategoryMap[platform])
      .filter(Boolean);

    if (!selectedCategories.length) return [];

    return subPlatformOptions
      .filter(
        (option) =>
          selectedCategories.includes(option.category) &&
          savedSubPlatforms.includes(option.name)
      )
      .map((option) => option.name);
  };

  const hasCompatiblePlatform = platforms?.some((platform) =>
    ['Web', 'Mobile Responsive', 'Native Mobile App', 'Hybrid Mobile App'].includes(platform)
  );

  const handlePlatformChange = (selectedPlatforms) => {
    setFieldValue('platforms', selectedPlatforms);

    const filteredSubPlatforms = getFilteredSubPlatforms(selectedPlatforms);
    const updatedSubPlatforms = (subPlatforms || []).filter((sub) =>
      filteredSubPlatforms.includes(sub)
    );

    setFieldValue('subPlatforms', updatedSubPlatforms);

    if (!selectedPlatforms.some((platform) => platformToCategoryMap[platform])) {
      setFieldValue('browsers', []);
    }
  };

  const filteredSubPlatformOptions = getFilteredSubPlatforms(platforms);
  const countryOptions = React.useMemo(() => {
    if (!projectConfig?.geographies?.length) return [];
    const formatted = formatCountryOptions(projectConfig.geographies);
    return formatted.map((item) => ({
      id: item.value,
      name: typeof item.display === 'string' ? item.display : String(item.value),
    }));
  }, [projectConfig?.geographies]);

  return (
    <>
      {projectConfig?.platforms?.length > 0 && (
        <Grid item xs={12} md={4}>
          {isEditing && !isCycleCompleted ? (
            <MultiSelectDropdown
              label="Testing Platform"
              required={isPlatformRequired}
              name="platforms"
              options={toOptions(projectConfig.platforms)}
              selectedValues={platforms || []}
              onChange={handlePlatformChange}
              placeholder="Select platforms"
              showSelectAll
            />
          ) : (
            <StaticField
              label="Testing Platform"
              value={platforms?.join(', ')}
              required={isPlatformRequired}
            />
          )}
        </Grid>
      )}

      {projectConfig?.sub_platforms?.length > 0 && filteredSubPlatformOptions?.length > 0 && (
        <Grid item xs={12} md={4}>
          {isEditing && !isCycleCompleted ? (
            <MultiSelectDropdown
              label="Supported Options"
              required={isPlatformRequired}
              name="subPlatforms"
              options={toOptions(filteredSubPlatformOptions)}
              selectedValues={subPlatforms || []}
              onChange={(value) => setFieldValue('subPlatforms', value)}
              placeholder="Select sub-platforms"
              showSelectAll
            />
          ) : (
            <StaticField
              label="Supported Options"
              value={subPlatforms?.join(', ')}
              required={isPlatformRequired}
            />
          )}
        </Grid>
      )}

      {hasCompatiblePlatform && browserOptions?.length > 0 && (
        <Grid item xs={12} md={4}>
          {isEditing && !isCycleCompleted ? (
            <MultiSelectDropdown
              label="Browsers"
              name="browsers"
              options={toOptions(browserOptions)}
              selectedValues={browsers || []}
              onChange={(value) => setFieldValue('browsers', value)}
              placeholder="Select browsers"
              showSelectAll
            />
          ) : (
            <StaticField label="Browsers" value={browsers?.join(', ')} />
          )}
        </Grid>
      )}
      {projectConfig?.multiple_geographies === 'Yes' && projectConfig?.geographies?.length > 0 && (
        <Grid item xs={12} md={4}>
          {isEditing && !isCycleCompleted ? (
            <MultiSelectDropdown
              label="Countries"
              name="geographies"
              options={countryOptions}
              selectedValues={geographies || []}
              onChange={(value) => setFieldValue('geographies', value)}
              placeholder="Select countries"
              searchable
              showSelectAll
            />
          ) : (
            <StaticField
              label="Countries"
              value={geographies?.length > 0 ? geographies.join(', ') : null}
            />
          )}
        </Grid>
      )}
    </>
  );
};

export default PlatformSelection;
